// src/games/Rebus/index.tsx
import { message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IoTriangleSharp } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';

import { GameHeader } from '../../components/GameHeader';
import { Sidebar } from '../../components/Sidebar';
import { useAuth } from '../../hooks/auth';
import { usePopup } from '../../hooks/popup';
import { FinishModal } from '../core/finish/FinishModal';
import { revealConfirm } from '../core/reveal/revealConfirm';
import { useGameSession } from '../core/session/useGameSession';
import { RebusLoad, finishRebus, loadRebusGame, rebusResult, saveRebusProgress } from './api';
import { AnswerSlots } from './AnswerSlots';
import {
  RebusAnswer,
  clearRebusAnswer,
  rebusMaxPoints,
  revealRebusAll,
  revealRebusLetter,
  revealRebusWord,
} from './rules';
import { Container, Content, GameContent } from './styles';

/**
 * Rota `/jogo/rebus/:id`. O `key` remonta a tela (e a sessão) quando o id muda com a rota
 * montada: sem isso, a resposta do jogo A seria salva e verificada no jogo B. A tela recebe
 * o id por prop, nunca do `useParams`.
 */
export const Rebus: React.FC = () => {
  const jogoId = Number(useParams().id);
  return <RebusScreen key={jogoId} jogoId={jogoId} />;
};

const RebusScreen = ({ jogoId }: { jogoId: number }) => {
  const { user } = useAuth();
  const userId = user?.id;
  const { mountPopup } = usePopup();
  const navigate = useNavigate();

  const [loaded, setLoaded] = useState<RebusLoad | null>(null);
  const [answer, setAnswer] = useState<RebusAnswer>({ input: '', reveladas: [] });
  const answerRef = useRef(answer);
  answerRef.current = answer;

  useEffect(() => {
    let alive = true;
    loadRebusGame(jogoId, userId)
      .then((res) => {
        if (!alive) return;
        if (!res) {
          message.warning('Este jogo ainda não tem conteúdo.');
          navigate('/');
          return;
        }
        setAnswer({ input: res.input, reveladas: res.reveladas });
        setLoaded(res);
      })
      .catch(() => {
        if (!alive) return;
        message.error('Não foi possível abrir o jogo. Tente de novo.');
        navigate('/');
      });
    return () => {
      alive = false;
    };
  }, [jogoId, userId, navigate]);

  const solucao = loaded?.solucao ?? '';

  const session = useGameSession({
    ready: loaded !== null,
    savedSeconds: loaded?.savedSeconds ?? 0,
    alreadyFinished: loaded?.finished ?? null,
    onSaveProgress: (seconds) =>
      userId !== undefined && loaded
        ? saveRebusProgress({ userId, jogoId, solucao: loaded.solucao, ...answerRef.current, seconds })
        : undefined,
  });
  const { finish, isPlaying } = session;
  const playingRef = useRef(isPlaying);
  playingRef.current = isPlaying;

  const finishWith = useCallback(
    (final: RebusAnswer) => {
      void finish(rebusResult(solucao, final.input, final.reveladas), async (seconds) => {
        if (userId === undefined) return;
        await finishRebus({ userId, jogoId, solucao, input: final.input, reveladas: final.reveladas, seconds });
      });
    },
    [finish, jogoId, solucao, userId]
  );

  const applyReveal = (reveal: (answer: RebusAnswer) => RebusAnswer): RebusAnswer => {
    const next = reveal(answerRef.current);
    answerRef.current = next;
    setAnswer(next);
    return next;
  };

  const askVerify = () =>
    mountPopup({
      title: 'Verificar resposta?',
      message: 'O jogo termina e o resultado vale para o ranking.',
      type: 'alegre',
      buttons: [
        { buttonText: 'Verificar', buttonFunction: () => finishWith(answerRef.current) },
        { buttonText: 'Cancelar', isPurple: true },
      ],
    });

  const askRevealLetter = () =>
    mountPopup(
      revealConfirm({
        scope: 'cell',
        unit: 'letra',
        title: 'Revelar letra?',
        onConfirm: () => applyReveal((a) => revealRebusLetter(solucao, a)),
      })
    );

  const askRevealWord = () =>
    mountPopup(
      revealConfirm({
        scope: 'word',
        unit: 'letra',
        title: 'Revelar palavra?',
        onConfirm: () => applyReveal((a) => revealRebusWord(solucao, a)),
      })
    );

  const askRevealAll = () =>
    mountPopup(
      revealConfirm({
        scope: 'all',
        unit: 'letra',
        title: 'Revelar resposta?',
        onConfirm: () => {
          // Recusado fora de 'playing': não mexe na resposta.
          if (!playingRef.current) return;
          finishWith(applyReveal(() => revealRebusAll(solucao)));
        },
      })
    );

  const askClear = () =>
    mountPopup({
      title: 'Limpar o jogo?',
      message: 'Sua resposta será apagada. As posições reveladas continuam sem valer pontos.',
      type: 'atencao',
      buttons: [
        { buttonText: 'Limpar', buttonFunction: () => setAnswer((current) => clearRebusAnswer(solucao, current)) },
        { buttonText: 'Cancelar', isPurple: true },
      ],
    });

  // Enquanto carrega não há ações (nem "Ver resultado" sem resultado).
  const menuItems =
    session.status === 'loading'
      ? []
      : isPlaying
        ? [
            { buttonText: 'Verificar resposta', buttonFunctions: askVerify },
            { buttonText: 'Revelar letra', buttonFunctions: askRevealLetter },
            { buttonText: 'Revelar palavra', buttonFunctions: askRevealWord },
            { buttonText: 'Revelar resposta', buttonFunctions: askRevealAll },
            { buttonText: 'Limpar jogo', buttonFunctions: askClear },
          ]
        : [{ buttonText: 'Ver resultado', buttonFunctions: session.showResult }];

  const maxScore = rebusMaxPoints(solucao);
  const currentScore = session.result?.points ?? 0;
  const rebus = loaded?.rebus;

  return (
    <Container>
      <GameHeader type="REBUS" maxScore={maxScore} currentScore={currentScore} seconds={session.seconds} menuItems={menuItems} />

      <Content>
        <GameContent>
          {rebus && <span>{rebus.numeracao}</span>}
          <div>
            <main>
              {rebus && <img src={rebus.imagem} alt="Rébus" />}

              <input
                disabled={!isPlaying}
                type="text"
                name="answer"
                maxLength={solucao.length || undefined}
                value={answer.input}
                onChange={(event) => {
                  const typed = event.target.value.toUpperCase();
                  setAnswer((current) => ({ ...current, input: typed }));
                }}
                placeholder="DIGITE AQUI SUA RESPOSTA"
              />
              {solucao && <AnswerSlots solucao={solucao} input={answer.input} reveladas={answer.reveladas} />}
            </main>

            <Sidebar
              type="REBUS"
              text={rebus?.numeracao}
              maxScore={maxScore}
              currentScore={currentScore}
              seconds={session.seconds}
              menuItems={menuItems}
            />
          </div>
        </GameContent>
        <p style={{ background: '#000' }}>
          VER MAIS COMENTÁRIOS <IoTriangleSharp size={12} />
        </p>
      </Content>

      <FinishModal
        visible={session.modalVisible}
        result={session.result}
        seconds={session.seconds}
        error={session.error}
        busy={session.status === 'finishing'}
        onRetry={session.retry}
        onClose={session.hideResult}
        onHome={() => navigate('/')}
      />
    </Container>
  );
};
